import React from 'react';

import { Icon, addWithConfig } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useCartItemsCount } from 'features/cart/hooks/use-cart-items-count';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { Box, Typography } from 'components/ui';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { t } from 'state/intl/translations';
import { ZIndex, getDesignToken, primitive, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

const StyledButton = addWithConfig(ActionButton).withConfig({
  _text: {
    color: primitive.$mayoEggWhite,
  },
  _hover: {
    _text: {
      color: primitive.$mayoEggWhite,
    },
  },
  _disabled: {
    _text: {
      color: primitive.$disabledText,
    },
  },
});

const Badge = ({ children }: React.PropsWithChildren) => (
  <Box style={styles.badge}>
    <Typography style={styles.badgeText}>{children}</Typography>
  </Box>
);

export const CartButton = () => {
  const { navigate } = useRouter();

  const numberOfItemsInCart = useCartItemsCount();
  const hideCartMenu = useIsMobileBreakpoint();

  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();

  const { openSignInModal, isAuthenticated } = useAuthContext();
  const { logExposure } = useExperiments();

  if (hideCartMenu) {
    return null;
  }

  const handleOnPress = () => {
    if (!isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);
    }

    if (isAuthenticated || isSignUpAfterCartEnabled) {
      navigate(routes.cart);
    } else {
      openSignInModal({
        navigateOnSuccessRoute: routes.cart,
      });
    }
  };

  return (
    <StyledButton
      keepIconSize
      variant={ActionButtonVariants.PRIMARY}
      size={ActionButtonSizes.SMALL}
      data-testid="cart-button-desktop"
      onPress={handleOnPress}
      startIcon={<Icon variant="cart" aria-hidden size={4} />}
      accessibilityLabel={t('viewCart')}
      disabled={numberOfItemsInCart === 0}
    >
      {!!numberOfItemsInCart && <Badge>{numberOfItemsInCart}</Badge>}

      {t('viewCart')}
    </StyledButton>
  );
};

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    backgroundColor: getDesignToken('color-contrast-background'),
    borderRadius: primitive.$borderRadius,
    bottom: spacing.$3,
    height: spacing.$5,
    justifyContent: 'center',
    position: 'absolute',
    right: -16,
    width: spacing.$5,
    zIndex: ZIndex.TOP,
  },
  badgeText: {
    color: getDesignToken('text-reversed'),
    fontSize: 13,
    textAlign: 'center',
  },
});
